import { useCallback, useMemo } from 'react';

import { sendOddsClickCustomEvent } from '@cx-modules/utils';

import { useSelector } from '../providers/StoreProvider';
import { type Odd } from '../types';

import { useBets } from './useBets';

export const useBetslip = ({
  betId,
  betOdds,
  bookieCode,
}: {
  betId: number;
  betOdds?: string | number;
  bookieCode: string;
}): { isBookieBetSelected: boolean; toggleBetslipBet: () => void } => {
  const selectedBets = useSelector(store => store.selectedBets);
  const addSelectedBet = useSelector(store => store.addSelectedBet);
  const removeSelectedBet = useSelector(store => store.removeSelectedBet);
  const marketName = useSelector(store => store.marketName);

  const isBookieBetSelected = useMemo(
    () => selectedBets.some(bet => bet.betId === betId && bet.bookieCode === bookieCode),
    [betId, selectedBets, bookieCode],
  );

  const bets = useBets();
  const betById = useMemo(() => bets.find(b => b.id === betId), [bets, betId]);

  const bookieBet = useMemo(
    () => betById?.odds.find(odd => odd.bookmakerCode === bookieCode) || ({} as Odd),
    [betById, bookieCode],
  );

  const removeFromBetslip = useCallback(() => {
    removeSelectedBet(betId);

    sendOddsClickCustomEvent({
      betLocation: 'odds-grid',
      betName: `${betById?.name} ${betById?.line?.name}`,
      betOdds: [bookieBet],
      bookieCode,
      event: 'remove_from_betslip',
      marketName,
      pageTitle: 'subevent-odds-grid',
    });
  }, [removeSelectedBet, betId, betById?.name, betById?.line?.name, bookieBet, bookieCode, marketName]);

  const addToBetslip = useCallback(() => {
    const existingBet = selectedBets.find(bet => bet.betId === betId);
    if (existingBet) {
      sendOddsClickCustomEvent({
        betLocation: 'odds-grid',
        betName: `${betById?.name} ${betById?.line?.name}`,
        betOdds: [bookieBet],
        bookieCode,
        event: 'odds_button_click_remove',
        marketName,
        pageTitle: 'subevent-odds-grid',
      });
    }

    addSelectedBet({ betId, bookieCode });

    sendOddsClickCustomEvent({
      betLocation: 'odds-grid',
      betName: `${betById?.name} ${betById?.line?.name}`,
      betOdds: [bookieBet],
      bookieCode,
      event: 'odds_button_click',
      marketName,
      pageTitle: 'subevent-odds-grid',
    });
  }, [selectedBets, addSelectedBet, betId, bookieCode, betById?.name, betById?.line?.name, bookieBet, marketName]);

  const toggleBetslipBet = useCallback(() => {
    try {
      window.bettingActions.addOrRemoveFromBetslip(
        [{ betId, selectedBookie: bookieCode }],
        [betId],
        bookieCode,
        betOdds?.toString(),
        `${betById?.name} ${betById?.line?.name}`,
        'odds-grid',
        'n/a',
        marketName,
        'n/a',
      );
    } catch (e) {
      console.error('useBetslip.toggleBetslipBet', e);
    }

    isBookieBetSelected ? removeFromBetslip() : addToBetslip();
  }, [
    isBookieBetSelected,
    removeFromBetslip,
    addToBetslip,
    betId,
    betOdds,
    bookieCode,
    betById?.name,
    betById?.line?.name,
    marketName,
  ]);

  return { isBookieBetSelected, toggleBetslipBet };
};
